import { http } from "./client";

// Approver chains, approval rules, org charts, overtime requests and the approver dashboard.
const data = (r) => r.data;
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && v !== false,
    ),
  );

/* ---------- Approver chains (admins/HR) ---------- */
export const approverApi = {
  // GET /approvers/employees?branchId&search&approverEmployeeId&missingOnly&page&limit
  getList: (filters = {}) =>
    http.get("/approvers/employees", { params: clean(filters) }).then(data),
  getForEmployee: (employeeId) =>
    http.get(`/approvers/employees/${employeeId}`).then(data),
  // Ordered level 1, 2, … (max 5). requestType null/omitted = the chain for every type
  // ([] = rules / default); DTR | OVERTIME | LEAVE | VALIDATION = an override for that
  // type only ([] removes the override).
  setForEmployee: (employeeId, approverEmployeeIds, requestType) =>
    http
      .put(`/approvers/employees/${employeeId}`, {
        approverEmployeeIds,
        ...(requestType ? { requestType } : {}),
      })
      .then(data),
  setForMany: (employeeIds, approverEmployeeIds, requestType) =>
    http
      .put("/approvers/employees", {
        employeeIds,
        approverEmployeeIds,
        ...(requestType ? { requestType } : {}),
      })
      .then(data),
};

/* ---------- Approval rules per request type (company-setup) ---------- */
export const approvalRulesApi = {
  // { requestTypes: [{ key, label, multiLevel }], ruleTypes: [{ type, label, params }], maxLevels }
  getOptions: () => http.get("/approval-rules/options").then(data),
  // employeeType: Internal | Deployed | omitted (all employees)
  // { companyId, companyName, employeeType, data: [{ requestType, label, multiLevel, employeeType,
  //   source: COMPANY|INHERITED|DEFAULT, appliesTo, inheritedFrom: { companyId, companyName, employeeType }, levels, updatedAt }] }
  getAll: (companyId, employeeType) =>
    http
      .get("/approval-rules", { params: clean({ companyId, employeeType }) })
      .then(data),
  // levels: [{ type, levelsUp?, search?, roleId?, employeeId? }] (400 → message[] "Level 2: …")
  save: (companyId, type, levels, employeeType) =>
    http
      .put(
        `/approval-rules/${companyId}/${type}`,
        { levels },
        { params: clean({ employeeType }) },
      )
      .then(data),
  // Back to the next rule in line (all-employees rule, parent company, default)
  reset: (companyId, type, employeeType) =>
    http
      .delete(`/approval-rules/${companyId}/${type}`, {
        params: clean({ employeeType }),
      })
      .then(data),
  // { employeeId, byType: [{ requestType, label, source: EMPLOYEE|RULE|DEFAULT, levels: [{ level, approvers }] }] }
  preview: (employeeId, type) =>
    http
      .get("/approval-rules/preview", { params: clean({ employeeId, type }) })
      .then(data),
};

/* ---------- Org charts ---------- */
export const orgChartApi = {
  // { companyId, unassignedEmployees, data: [branch node with children] }
  getBranches: (companyId) =>
    http
      .get("/org-chart/branches", { params: clean({ companyId }) })
      .then(data),
  // { companyId, data: [role node with holders (first 20) and children] }
  getRoles: (companyId) =>
    http.get("/org-chart/roles", { params: clean({ companyId }) }).then(data),
};

/* ---------- Overtime requests ---------- */
export const overtimeApi = {
  // GET /overtime?status (default PENDING)&from&to&branchId&employeeId&search&page&limit
  getList: (filters = {}) =>
    http.get("/overtime", { params: clean(filters) }).then(data),
  getById: (id) => http.get(`/overtime/${id}`).then(data),
  // { action: APPROVE | REJECT, approvedMinutes?, remarks? (required for REJECT), allLevels? (HR) }
  // Multi-level: stays PENDING until the last level; 403 "Waiting for the level N approver" = not my turn
  review: (id, body) =>
    http.post(`/overtime/${id}/review`, clean(body)).then(data),
  // HR (overtime modify, COMPANY scope): file for an employee, past times allowed (filedLate)
  // { employeeId, date, startTime, endTime (ISO +08:00), reason }
  fileOnBehalf: (body) => http.post("/overtime/on-behalf", body).then(data),
};

/* ---------- Attendance corrections (dtr module: same people as DTR approvals) ---------- */
export const correctionsApi = {
  // GET /attendance/corrections?status&branchId&search&page&limit → { data, total, page, limit }
  getList: (filters = {}) =>
    http.get("/attendance/corrections", { params: clean(filters) }).then(data),
  // { action: APPROVE | REJECT, remarks? (required for REJECT) }; the last level applies it
  review: (id, body) =>
    http.post(`/attendance/corrections/${id}/review`, clean(body)).then(data),
};

/* ---------- Approver dashboard ---------- */
export const dashboardMeApi = {
  // GET /dashboard/me → { approvals: { isApprover, pendingValidations, pendingLeave, pendingCorrections,
  //   pendingOvertime, pendingEvaluations, total, requests: [{ type, label, count, link }] }, … }
  getMe: () => http.get("/dashboard/me").then(data),
  // GET /dashboard/approvals → the same `approvals` object, for any signed-in user (no employee needed).
  // companyId / branchId only narrow (company tree / branch tree); omit them for everything.
  getApprovals: (params) =>
    http.get("/dashboard/approvals", { params: clean(params) }).then(data),
};
